import React, { useCallback, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Box,
  Paper,
  MenuItem,
  InputLabel,
  Stack,
  DialogTitle,
  Select,
  FormControl,
  IconButton,
  useMediaQuery,
  useTheme,
  FormHelperText,
} from "@mui/material";

import { ReactComponent as Close } from "../../assets/icons/close.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-icon.svg";
import { ReactComponent as Edit } from "../../assets/icons/edit.svg";
import { ReactComponent as Plus } from "../../assets/icons/plus.svg";
import { ReactComponent as Minus } from "../../assets/icons/minus.svg";
import { allCategoriesSubCategories } from "../../utils/data";

import Button from "../Button";

const ProductModal = ({ open, setOpen, handleSubmit }) => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const validationSchema = Yup.object({
    brandName: Yup.string().required("Brand name is required"),
    category: Yup.string().required("Category is required"),
    productName: Yup.string().required("Product name is required"),
    retailPrice: Yup.number()
      .typeError("Price must be a number")
      .positive("Price must be positive")
      .required("Retail price is required"),
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .positive("Quantity must be positive")
      .integer("Quantity must be a whole number")
      .required("Quantity is required"),
    image: Yup.mixed().required("Product image is required"),
    description: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      brandName: "",
      category: "",
      productName: "",
      retailPrice: "",
      quantity: 1,
      description: "",
      image: null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (handleSubmit) {
          await handleSubmit(values, formik.resetForm);
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        formik.setFieldError(
          "image",
          "Invalid file type. Only JPG, JPEG, PNG, and PDF are allowed."
        );
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        // Create preview URL and set the file in formik
        formik.setFieldValue(
          "image",
          Object.assign(file, { preview: URL.createObjectURL(file) })
        );
        formik.setFieldTouched("image", true, false); // Mark as touched but avoid validation immediately
      }
    },
    [formik]
  );

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/jpg": [],
      "image/png": [],
      "application/pdf": [],
    },
    maxSize: 10485760,
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  // Clean up image preview URL on unmount
  useEffect(() => {
    return () => {
      if (formik.values.image && formik.values.image.preview) {
        URL.revokeObjectURL(formik.values.image.preview);
      }
    };
  }, [formik.values.image]);

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isSmallScreen ? "sm" : "md"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: isXsScreen ? 1 : 2,
          boxShadow: 2,
          maxHeight: "90vh",
          overflow: "visible",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          p: isXsScreen ? 2 : 3,
        }}
      >
        <Typography sx={{ fontSize: isXsScreen ? 16 : 18, fontWeight: 500 }}>
          Add Product
        </Typography>
        <Box sx={{ cursor: "pointer", ml: "auto" }} onClick={handleClose}>
          <Close />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: isXsScreen ? 2 : 3,
          overflow: "auto",
          maxHeight: { xs: "70vh", sm: "75vh", md: "80vh" },
        }}
      >
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          noValidate
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            mb: 3,
          }}
        >
          {/* Left side - Image upload */}
          <Box
            sx={{
              width: { xs: "100%", md: "auto" },
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              mb: { xs: 2, md: 0 },
            }}
          >
            <div {...getRootProps({ className: "dropzone" })}>
              <input {...getInputProps()} />
              <Paper
                elevation={0}
                variant="outlined"
                onClick={formik.values.image ? undefined : openFileDialog}
                sx={{
                  width: isXsScreen ? "100%" : isSmallScreen ? 250 : 350,
                  height: isXsScreen ? 200 : isSmallScreen ? 250 : 350,
                  maxWidth: "100%",
                  bgcolor: "#f0ecf4",
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: formik.values.image ? "default" : "pointer",
                  position: "relative",
                  borderColor:
                    formik.touched.image && formik.errors.image
                      ? "error.main"
                      : "inherit",
                  borderWidth:
                    formik.touched.image && formik.errors.image ? 2 : 1,
                  overflow: "hidden", // Prevent content from spilling out
                }}
              >
                {formik.values.image ? (
                  <Box
                    sx={{ width: "100%", height: "100%", position: "relative" }}
                  >
                    {formik.values.image.type === "application/pdf" ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <iframe
                          src={`${formik.values.image.preview}#toolbar=0`}
                          style={{
                            width: "100%",
                            height: "600px",
                            border: "none",
                            overflow: "hidden",
                            scrollbarWidth: "none", // Hides scrollbar for Firefox
                          }}
                          title="PDF Preview"
                          width="100%"
                          height="100%"
                        />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={formik.values.image.preview}
                        alt="Preview"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    {/* Edit Button */}
                    <IconButton
                      onClick={openFileDialog}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "white",
                        "&:hover": { bgcolor: "grey.200" },
                      }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "100%",
                      width: "100%",
                      p: 2,
                    }}
                  >
                    <div></div>
                    <div className="flex flex-col items-center mt-20">
                      <Box
                        sx={{
                          width: isXsScreen ? 30 : 36,
                          height: isXsScreen ? 30 : 36,
                          borderRadius: "50%",
                          bgcolor: "#333",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <UploadIcon />
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 500, textDecoration: "underline" }}
                        >
                          Upload
                        </Typography>
                        <Typography component="span" color="text.secondary">
                          {" or drag and drop it here"}
                        </Typography>
                      </Box>
                    </div>
                    <div>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          Allowed formats: PDF, JPG, JPEG, PNG
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          Maximum file size: 10 MB
                        </Typography>
                      </Box>
                    </div>
                  </Box>
                )}
              </Paper>
            </div>
            {formik.touched.image && formik.errors.image && (
              <FormHelperText
                error
                sx={{ mt: 1, textAlign: { xs: "center", md: "left" } }}
              >
                {formik.errors.image}
              </FormHelperText>
            )}

            {/* Quantity field under image */}
            <Box sx={{ width: "50%", mt: 2 }}>
              <InputLabel
                htmlFor="quantity"
                sx={{
                  mb: 0.5,
                  fontWeight: 400,
                  color: "text.primary",
                  fontSize: isXsScreen ? 13 : 14,
                }}
              >
                Quantity *
              </InputLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: formik.touched.quantity && formik.errors.quantity 
                    ? "2px solid" 
                    : "1px solid",
                  borderColor: formik.touched.quantity && formik.errors.quantity 
                    ? "error.main" 
                    : "rgba(0, 0, 0, 0.23)",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  height: 40, // Match other field heights
                  "&:hover": {
                    borderColor: formik.touched.quantity && formik.errors.quantity 
                      ? "error.main" 
                      : "rgba(0, 0, 0, 0.87)",
                  },
                  "&:focus-within": {
                    borderColor: formik.touched.quantity && formik.errors.quantity 
                      ? "error.main" 
                      : "primary.main",
                    borderWidth: "2px",
                  },
                }}
              >
                <IconButton
                  onClick={() => {
                    const newValue = Math.max(1, parseInt(formik.values.quantity || 0) - 1);
                    formik.setFieldValue("quantity", newValue);
                    formik.setFieldTouched("quantity", true);
                  }}
                  disabled={formik.values.quantity <= 1}
                  size="small"
                  sx={{
                    color: formik.values.quantity <= 1 ? "action.disabled" : "text.primary",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    ml: 0.5,
                  }}
                >
                  <Minus />
                </IconButton>
                <TextField
                  id="quantity"
                  name="quantity"
                  value={formik.values.quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    formik.setFieldValue("quantity", Math.min(10000, Math.max(1, value)));
                  }}
                  onBlur={formik.handleBlur}
                  variant="standard"
                  size="small"
                  type="number"
                  inputProps={{
                    min: 1,
                    max: 10000,
                    style: { textAlign: "center" },
                  }}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: isXsScreen ? 14 : 16,
                      fontWeight: 500,
                      "& input": {
                        py: 0.5,
                        px: 1,
                      },
                    },
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      bgcolor: "transparent",
                    },
                  }}
                />
                <IconButton
                  onClick={() => {
                    const newValue = Math.min(10000, parseInt(formik.values.quantity || 0) + 1);
                    formik.setFieldValue("quantity", newValue);
                    formik.setFieldTouched("quantity", true);
                  }}
                  disabled={formik.values.quantity >= 10000}
                  size="small"
                  sx={{
                    color: formik.values.quantity >= 10000 ? "action.disabled" : "text.primary",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    mr: 0.5,
                  }}
                >
                  <Plus />
                </IconButton>
              </Box>
              {formik.touched.quantity && formik.errors.quantity && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {formik.errors.quantity}
                </FormHelperText>
              )}
            </Box>
          </Box>

          {/* Right side - Form fields */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Stack spacing={isXsScreen ? 1.5 : 2}>
              <Box>
                <InputLabel
                  htmlFor="brandName"
                  sx={{
                    mb: 0.5,
                    fontWeight: 400,
                    color: "text.primary",
                    fontSize: isXsScreen ? 13 : 14,
                  }}
                >
                  Brand Name *
                </InputLabel>
                <TextField
                  id="brandName"
                  name="brandName"
                  fullWidth
                  placeholder="Enter Brand Name"
                  value={formik.values.brandName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.brandName && Boolean(formik.errors.brandName)
                  }
                  helperText={
                    formik.touched.brandName && formik.errors.brandName
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: 0.5 },
                  }}
                  required
                />
              </Box>

              <Box>
                <InputLabel
                  htmlFor="category"
                  sx={{
                    mb: 0.5,
                    fontWeight: 400,
                    color: "text.primary",
                    fontSize: isXsScreen ? 13 : 14,
                  }}
                >
                  Category *
                </InputLabel>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.category && Boolean(formik.errors.category)
                  }
                >
                  <Select
                    id="category"
                    name="category"
                    displayEmpty
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    sx={{
                      borderRadius: 2,
                      ".MuiSelect-select": {
                        borderRadius: "inherit",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderRadius: "inherit",
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            Select One
                          </Typography>
                        );
                      }
                      return selected;
                    }}
                    required
                  >
                    <MenuItem value="" disabled>
                      Select One
                    </MenuItem>
                    {allCategoriesSubCategories.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.category && formik.errors.category && (
                    <FormHelperText>{formik.errors.category}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box>
                <InputLabel
                  htmlFor="productName"
                  sx={{
                    mb: 0.5,
                    fontWeight: 400,
                    color: "text.primary",
                    fontSize: isXsScreen ? 13 : 14,
                  }}
                >
                  Product Name *
                </InputLabel>
                <TextField
                  id="productName"
                  name="productName"
                  fullWidth
                  placeholder="Enter Name"
                  value={formik.values.productName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.productName &&
                    Boolean(formik.errors.productName)
                  }
                  helperText={
                    formik.touched.productName && formik.errors.productName
                  }
                  variant="outlined"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: 0.5 },
                  }}
                  required
                />
              </Box>

              <Box>
                <InputLabel
                  htmlFor="retailPrice"
                  sx={{
                    mb: 0.5,
                    fontWeight: 400,
                    color: "text.primary",
                    fontSize: isXsScreen ? 13 : 14,
                  }}
                >
                  Retail Price *
                </InputLabel>
                <TextField
                  id="retailPrice"
                  name="retailPrice"
                  fullWidth
                  placeholder="Enter Cost"
                  value={formik.values.retailPrice}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.retailPrice &&
                    Boolean(formik.errors.retailPrice)
                  }
                  helperText={
                    formik.touched.retailPrice && formik.errors.retailPrice
                  }
                  variant="outlined"
                  size="small"
                  type="number"
                  InputProps={{
                    sx: { borderRadius: 0.5 },
                    inputProps: { min: 0, step: "0.01" },
                  }}
                  required
                />
              </Box>
              <Box>
                <InputLabel
                  htmlFor="description"
                  sx={{
                    mb: 0.5,
                    fontWeight: 400,
                    color: "text.primary",
                    fontSize: isXsScreen ? 13 : 14,
                  }}
                >
                  Product Description
                </InputLabel>
                <TextField
                  id="description"
                  name="description"
                  fullWidth
                  multiline
                  rows={isXsScreen ? 3 : 4}
                  placeholder="Enter additional product information..."
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: 0.5 },
                  }}
                />
              </Box>
              <Box sx={{ mt: isXsScreen ? 1 : 2 }}>
                <Button
                  className="w-full"
                  small
                  type="submit"
                  onClick={formik.handleSubmit}
                  disabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                >
                  {"Save Changes"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
